import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

// ---- Global enquiry dialog trigger (custom event based) ----
export const openEnquiry = (enquiryType = "General") => {
  window.dispatchEvent(new CustomEvent("open-enquiry", { detail: { enquiryType } }));
};

// ---- Scroll-reveal wrapper ----
export const Reveal = ({ children, delay = 0, y = 24, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y, filter: "blur(8px)" }}
      animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ---- SEO-optimised, lazy-loaded image ----
export const SmartImage = ({ image, className = "", imgClassName = "", caption = false, priority = false, sizes }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <figure className={`relative overflow-hidden ${className}`} data-filename={image.filename}>
      <img
        src={image.src}
        alt={image.alt}
        title={image.title}
        width="1200"
        height="800"
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={priority ? "high" : "auto"}
        sizes={sizes}
        onLoad={() => setLoaded(true)}
        className={`h-full w-full object-cover transition-all duration-700 ${loaded ? "blur-0 scale-100" : "blur-xl scale-105"} ${imgClassName}`}
      />
      {caption && (
        <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 text-xs text-white/70">
          {image.caption}
        </figcaption>
      )}
    </figure>
  );
};

// ---- Section heading block ----
export const SectionHeading = ({ eyebrow, title, sub, center = false, as: Tag = "h2" }) => (
  <div className={`max-w-3xl ${center ? "mx-auto text-center" : ""}`}>
    {eyebrow && (
      <span className="text-sm uppercase tracking-[0.2em] text-vivid-cyan font-medium">{eyebrow}</span>
    )}
    <Tag className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
      {title}
    </Tag>
    {sub && <p className="mt-5 text-base sm:text-lg leading-relaxed text-white/70">{sub}</p>}
  </div>
);

// ---- Primary / secondary buttons ----
export const PrimaryButton = ({ children, className = "", ...props }) => (
  <button
    {...props}
    className={`inline-flex items-center justify-center gap-2 rounded-full bg-vivid-magenta px-8 py-4 font-medium text-white shadow-[0_0_24px_rgba(255,0,122,0.4)] transition-all duration-300 hover:bg-vivid-magentaHover hover:-translate-y-1 disabled:opacity-60 disabled:hover:translate-y-0 ${className}`}
  >
    {children}
  </button>
);

export const SecondaryButton = ({ children, className = "", ...props }) => (
  <button
    {...props}
    className={`inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-8 py-4 font-medium text-white backdrop-blur-md transition-all duration-300 hover:bg-white/10 hover:-translate-y-1 ${className}`}
  >
    {children}
  </button>
);

export const useCountUp = () => {};
